import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <main className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-surface-dark text-text-dark py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Crafting Timeless
              <br />
              <span className="text-accent-line-solid">Elegance</span>
            </h1>
            <p className="text-subtle-dark text-lg leading-relaxed">
              At The Fashion House, we believe in the power of exceptional craftsmanship 
              and timeless design. Every piece in our collection tells a story of dedication, 
              quality, and refined taste.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-coconut">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-accent-ui text-sm font-medium uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mt-4 mb-6">
                Born from a Passion for Quality
              </h2>
              <div className="space-y-4 text-text-subtle leading-relaxed">
                <p>
                  Founded in 2020, The Fashion House emerged from a simple yet profound belief: 
                  that exceptional fashion should be accessible to all who appreciate quality and design.
                </p>
                <p>
                  Our journey began in a small studio where our founders, driven by their love 
                  for timeless elegance, curated their first collection of handpicked garments 
                  from the finest ateliers across Europe.
                </p>
                <p>
                  Today, we continue that tradition, working directly with skilled artisans 
                  and sustainable manufacturers to bring you pieces that transcend seasons 
                  and trends.
                </p>
              </div>
            </div>
            <div className="aspect-[4/5] rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1445205170230-053b83016050?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxmYXNoaW9uJTIwY2xvdGhpbmd8ZW58MHx8fHwxNzY4MjMzNzk4fDA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Fashion atelier"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Our Values
            </h2>
            <p className="text-text-subtle max-w-lg mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Quality First',
                description: 'Every piece is crafted from the finest materials, ensuring longevity and comfort that lasts for years.'
              },
              {
                title: 'Sustainable Practice',
                description: 'We partner with ethical manufacturers and use eco-friendly packaging to minimize our environmental footprint.'
              },
              {
                title: 'Timeless Design',
                description: 'Our collections focus on classic silhouettes and versatile pieces that remain stylish season after season.'
              }
            ].map((value, index) => (
              <div key={index} className="text-center p-8">
                <div className="w-16 h-16 bg-accent-ui/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-display text-2xl font-bold text-accent-ui">{index + 1}</span>
                </div>
                <h3 className="font-display text-xl font-semibold text-text-primary mb-3">
                  {value.title}
                </h3>
                <p className="text-text-subtle leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-coconut">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-text-primary mb-6">
            Experience the Difference
          </h2>
          <p className="text-text-subtle text-lg mb-8">
            Discover our collection and find pieces that speak to your personal style.
          </p>
          <Link to="/shop">
            <Button className="bg-accent-ui hover:bg-accent-ui/90 text-white h-12 px-8 text-base">
              Shop Now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
